// ===========================================
// Student Streak Chart Component
// ===========================================
// Line chart showing student practice streak trends over time

import { Card, CardContent, CardHeader, Typography, Box, Divider, CircularProgress } from '@mui/material';
import { LocalFireDepartment as StreakIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { StudentStreakData } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface StudentStreakChartProps {
  data: StudentStreakData[];
  loading?: boolean;
}

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Card sx={{ p: 1.5, minWidth: 200 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Avg Streak:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.averageStreak.toFixed(1)} days
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Active Students:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.activeStudents}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption">Practice Days:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {data.totalPracticeDays}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function StudentStreakChart({ data, loading }: StudentStreakChartProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StreakIcon color="primary" />
              <Typography variant="h6">Student Practice Streaks</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StreakIcon color="primary" />
              <Typography variant="h6">Student Practice Streaks</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No streak data available for the selected period
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StreakIcon color="primary" />
            <Typography variant="h6">Student Practice Streaks</Typography>
          </Box>
        }
        subheader={`Showing ${data.length} week${data.length === 1 ? '' : 's'} of streak data`}
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="weekLabel"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              label={{ value: 'Days', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="averageStreak"
              name="Average Streak (days)"
              stroke="#f44336"
              strokeWidth={2}
              dot={{ fill: '#f44336', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
